import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckSquare,
  X,
  Plus,
  Calendar,
  User,
  Clock,
  CheckCircle,
  Circle,
  Search,
  Trash2,
  ListTodo,
  ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import { taskService } from '../utils/tasks/taskService';
import type { Task, CreateTaskInput } from '../types/task';

interface TaskManagerProps {
  currentUserId: string;
  currentUserName: string;
  wells: any[];
  team?: Array<{ id: string; name: string; role: string }>;
  onClose?: () => void;
  initialWellId?: string | null;
  isDarkMode?: boolean;
}

export default function TaskManager({
  currentUserId,
  currentUserName,
  wells,
  team = [],
  onClose,
  initialWellId,
  isDarkMode = false
}: TaskManagerProps) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [filteredTasks, setFilteredTasks] = useState<Task[]>([]);
  const [showNewTask, setShowNewTask] = useState(!!initialWellId);
  const [filterStatus, setFilterStatus] = useState<'all' | 'todo' | 'in_progress' | 'completed'>('all');
  const [filterAssignee, setFilterAssignee] = useState<'all' | 'me' | 'others'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [newTask, setNewTask] = useState<Partial<CreateTaskInput>>({
    title: '',
    description: '',
    assignedTo: currentUserId,
    assignedToName: currentUserName,
    priority: 'medium',
    wellId: initialWellId || undefined,
  });

  // Real-time subscription to tasks
  useEffect(() => {
    setIsLoading(true);

    const unsubscribe = taskService.subscribeToTasks((updatedTasks) => {
      setTasks(updatedTasks);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [tasks, filterStatus, filterAssignee, searchQuery]);

  const applyFilters = () => {
    let filtered = [...tasks];

    // Status filter - convert underscore format
    if (filterStatus !== 'all') {
      const statusMap = {
        'todo': 'todo',
        'in-progress': 'in_progress',
        'completed': 'completed'
      };
      // @ts-ignore
      filtered = filtered.filter(t => t.status === statusMap[filterStatus]);
    }

    // Assignee filter
    if (filterAssignee === 'me') {
      filtered = filtered.filter(t => t.assignedTo === currentUserId);
    } else if (filterAssignee === 'others') {
      filtered = filtered.filter(t => t.assignedTo !== currentUserId);
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        t =>
          t.title.toLowerCase().includes(query) ||
          t.description.toLowerCase().includes(query) ||
          t.wellName?.toLowerCase().includes(query) ||
          t.tags?.some(tag => tag.toLowerCase().includes(query))
      );
    }

    setFilteredTasks(filtered);
  };

  const handleCreateTask = async () => {
    if (!newTask.title || !newTask.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const taskInput: CreateTaskInput = {
        title: newTask.title,
        description: newTask.description,
        priority: newTask.priority || 'medium',
        assignedTo: newTask.assignedTo || currentUserId,
        assignedToName: newTask.assignedToName || currentUserName,
        wellId: newTask.wellId,
        wellName: newTask.wellId ? wells.find(w => w.id === newTask.wellId)?.name : undefined,
        dueDate: newTask.dueDate,
        notes: newTask.notes,
        tags: newTask.tags,
        estimatedHours: newTask.estimatedHours,
      };

      await taskService.createTask(taskInput, currentUserId, currentUserName);

      toast.success('Task created successfully');
      setShowNewTask(false);
      setNewTask({
        title: '',
        description: '',
        assignedTo: currentUserId,
        assignedToName: currentUserName,
        priority: 'medium',
      });
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error('Failed to create task');
    }
  };

  const handleStatusChange = async (taskId: string, newStatus: Task['status']) => {
    try {
      await taskService.updateTaskStatus(taskId, newStatus, currentUserId, currentUserName);
      toast.success('Task status updated');
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task status');
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!confirm('Are you sure you want to delete this task?')) {
      return;
    }

    try {
      await taskService.deleteTask(taskId);
      toast.success('Task deleted successfully');
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task');
    }
  };

  const getStatusIcon = (status: Task['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-emerald-500" />;
      case 'in_progress':
        return <Circle className="h-5 w-5 text-blue-500 fill-blue-500/20" />;
      case 'todo':
        return <Circle className={`h-5 w-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />;
      case 'cancelled':
        return <X className="h-5 w-5 text-red-400" />;
      default:
        return <Circle className="h-5 w-5 text-gray-400" />;
    }
  };

  const getPriorityColor = (priority: Task['priority']) => {
    if (isDarkMode) {
      switch (priority) {
        case 'urgent': return 'bg-red-900/50 text-red-300 border-red-800';
        case 'high': return 'bg-orange-900/50 text-orange-300 border-orange-800';
        case 'medium': return 'bg-yellow-900/50 text-yellow-300 border-yellow-800';
        case 'low': return 'bg-slate-800 text-slate-300 border-slate-700';
      }
    }
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-gray-100 text-gray-800 border-gray-200';
    }
    return '';
  };

  const isOverdue = (task: Task) => {
    return task.dueDate && task.status !== 'completed' && task.status !== 'cancelled' && new Date(task.dueDate) < new Date();
  };

  const formatDueDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    const now = Date.now();
    const diff = date.getTime() - now;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (diff < 0) return 'Overdue';
    if (hours < 24) return `Due in ${hours}h`;
    return `Due in ${days}d`;
  };

  const inputClass = isDarkMode
    ? 'bg-slate-800 border-slate-700 text-white placeholder:text-gray-500'
    : 'bg-white border-gray-200';

  const selectClass = isDarkMode
    ? 'bg-slate-800 border-slate-700 text-white'
    : 'bg-white border-gray-200';

  return (
    <div className={`fixed inset-0 z-50 flex flex-col ${isDarkMode ? 'bg-gradient-to-b from-slate-900 via-slate-950 to-slate-900' : 'bg-gray-50'}`}>

      {/* Header */}
      <div className={`p-4 shadow-lg z-10 ${isDarkMode ? 'bg-slate-900 border-b border-slate-800' : 'bg-gradient-to-r from-emerald-600 to-emerald-700 text-white'}`}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-xl ${isDarkMode ? 'bg-emerald-500/20' : 'bg-white/20'}`}>
              <CheckSquare className={`h-6 w-6 ${isDarkMode ? 'text-emerald-500' : 'text-white'}`} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Task Manager</h2>
              <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-emerald-100'}`}>
                {tasks.filter(t => t.status !== 'completed').length} active tasks
              </p>
            </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className={`p-2 rounded-full transition-colors ${isDarkMode ? 'hover:bg-slate-800 text-gray-400 hover:text-white' : 'hover:bg-white/20 active:bg-white/30 text-white'}`}
            >
              <X className="h-6 w-6" />
            </button>
          )}
        </div>
      </div>

      {/* Filters & Search */}
      <div className={`p-4 space-y-3 border-b ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-100'}`}>
        <div className="relative">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search tasks by title, well, or tag..."
            className={`pl-10 h-10 ${inputClass}`}
          />
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex space-x-2 overflow-x-auto pb-1 scrollbar-none">
            {[
              { id: 'all', label: 'All Tasks' },
              { id: 'todo', label: 'To Do' },
              { id: 'in-progress', label: 'In Progress' },
              { id: 'completed', label: 'Completed' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setFilterStatus(tab.id as any)}
                className={`px-4 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-colors border ${filterStatus === tab.id
                  ? 'bg-emerald-600 border-emerald-600 text-white'
                  : isDarkMode
                    ? 'bg-slate-800 border-slate-700 text-gray-400 hover:bg-slate-700 hover:text-white'
                    : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="flex space-x-2">
            {[
              { id: 'all', label: 'Everyone' },
              { id: 'me', label: 'Assigned to Me' },
              { id: 'others', label: 'Team' }
            ].map((filter) => (
              <button
                key={filter.id}
                onClick={() => setFilterAssignee(filter.id as any)}
                className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors ${filterAssignee === filter.id
                  ? isDarkMode ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-700 border border-blue-200'
                  : isDarkMode
                    ? 'bg-slate-800 text-gray-400 hover:bg-slate-700'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tasks List */}
      <div className={`flex-1 overflow-y-auto p-4 space-y-3 ${isDarkMode ? '' : 'bg-gray-50'}`}>
        <AnimatePresence mode="popLayout">
          {isLoading ? (
            <div className="text-center py-20">
              <div className="animate-spin h-12 w-12 border-4 border-emerald-600 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className={isDarkMode ? 'text-gray-500' : 'text-gray-500'}>Loading tasks...</p>
            </div>
          ) : filteredTasks.length === 0 ? (
            <div className="text-center py-20 opacity-60">
              <ListTodo className={`h-16 w-16 mx-auto mb-4 ${isDarkMode ? 'text-slate-800' : 'text-gray-300'}`} />
              <p className={isDarkMode ? 'text-gray-500' : 'text-gray-500'}>No tasks matching your filters</p>
            </div>
          ) : (
            filteredTasks.map((task, index) => (
              <motion.div
                key={task.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card
                  className={`border group transition-all hover:shadow-md ${isOverdue(task)
                    ? isDarkMode ? 'border-red-900 bg-red-900/10' : 'border-red-300 bg-red-50'
                    : isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-100'
                    }`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      {/* Status Toggle */}
                      <button
                        onClick={() =>
                          handleStatusChange(
                            task.id,
                            task.status === 'completed' ? 'todo' : task.status === 'in_progress' ? 'completed' : 'in_progress'
                          )
                        }
                        className="mt-1 transition-transform active:scale-90"
                      >
                        {getStatusIcon(task.status)}
                      </button>

                      <div className="flex-1 min-w-0">
                        {/* Title & Header */}
                        <div className="flex items-start justify-between mb-1">
                          <div>
                            <h3 className={`font-semibold text-lg leading-tight mb-1 ${task.status === 'completed'
                              ? isDarkMode ? 'line-through text-gray-600' : 'line-through text-gray-400'
                              : isDarkMode ? 'text-gray-100' : 'text-gray-900'
                              }`}>
                              {task.title}
                            </h3>
                            <div className="flex flex-wrap gap-2 mb-2">
                              <Badge className={`border ${getPriorityColor(task.priority)}`}>
                                {task.priority}
                              </Badge>
                              {task.wellName && (
                                <Badge variant="outline" className={isDarkMode ? 'text-gray-400 border-slate-700' : 'text-gray-600 border-gray-200'}>
                                  {task.wellName}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Description */}
                        {task.description && (
                          <p className={`text-sm mb-3 line-clamp-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            {task.description}
                          </p>
                        )}

                        {/* Footer Meta */}
                        <div className={`flex flex-wrap items-center gap-4 text-xs pt-3 border-t ${isDarkMode ? 'border-slate-800' : 'border-gray-50'}`}>
                          <div className={`flex items-center gap-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            <User className="h-3.5 w-3.5" />
                            <span>{task.assignedToName}</span>
                          </div>

                          {task.dueDate && (
                            <div className={`flex items-center gap-1.5 ${isOverdue(task) ? 'text-red-500 font-bold' : isDarkMode ? 'text-gray-400' : 'text-gray-500'
                              }`}>
                              <Calendar className="h-3.5 w-3.5" />
                              <span>{formatDueDate(task.dueDate)}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Delete Action (Top Right) */}
                      {task.status !== 'completed' && (
                        <button
                          onClick={() => handleDeleteTask(task.id)}
                          className={`opacity-0 group-hover:opacity-100 transition-opacity p-2 rounded-lg ${isDarkMode ? 'hover:bg-red-900/30 text-slate-600 hover:text-red-400' : 'hover:bg-red-50 text-gray-300 hover:text-red-500'}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>

                    {/* Quick Actions */}
                    {task.status !== 'completed' && task.status !== 'cancelled' && (
                      <div className="flex gap-2 mt-4 pl-9">
                        {task.status === 'todo' && (
                          <Button
                            onClick={() => handleStatusChange(task.id, 'in_progress')}
                            size="sm"
                            variant="outline"
                            className={`h-8 text-xs ${isDarkMode ? 'border-slate-700 hover:bg-slate-800 text-gray-300' : ''}`}
                          >
                            Start Task
                          </Button>
                        )}
                        {task.status === 'in_progress' && (
                          <Button
                            onClick={() => handleStatusChange(task.id, 'completed')}
                            size="sm"
                            className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white border-0"
                          >
                            Complete
                          </Button>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))
          )}
        </AnimatePresence>
      </div>

      {/* Add Task Area */}
      <div className={`border-t p-4 z-20 ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
        {!showNewTask ? (
          <Button
            onClick={() => setShowNewTask(true)}
            className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg text-lg font-bold"
          >
            <Plus className="h-5 w-5 mr-2" />
            Create New Task
          </Button>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            <div className="flex items-center justify-between">
              <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>New Task Details</h3>
              <button onClick={() => setShowNewTask(false)} className={isDarkMode ? 'text-gray-500 hover:text-white' : 'text-gray-400 hover:text-gray-900'}>
                <X className="h-5 w-5" />
              </button>
            </div>

            <Input
              value={newTask.title}
              onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
              placeholder="Task title"
              className={inputClass}
              autoFocus
            />

            <Textarea
              value={newTask.description}
              onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
              placeholder="Description"
              rows={2}
              className={inputClass}
            />

            <div className="grid grid-cols-2 gap-3">
              <select
                value={newTask.wellId || ''}
                onChange={(e) => setNewTask({ ...newTask, wellId: e.target.value })}
                className={`h-10 px-3 rounded-lg ${selectClass}`}
              >
                <option value="">No Location</option>
                {wells.map(well => (
                  <option key={well.id} value={well.id}>
                    {well.name}
                  </option>
                ))}
              </select>

              <select
                value={newTask.priority || 'medium'}
                onChange={(e) => setNewTask({ ...newTask, priority: e.target.value as any })}
                className={`h-10 px-3 rounded-lg ${selectClass}`}
              >
                <option value="low">Low Priority</option>
                <option value="medium">Medium Priority</option>
                <option value="high">High Priority</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            <div className="flex gap-3 pt-2">
              <Button onClick={handleCreateTask} className="flex-1 h-12 bg-emerald-600 hover:bg-emerald-700 font-bold text-white">
                Create Task
              </Button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
